import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import {
  fetchBookingRecords,
  fetchCoachSlots,
  modifyBookings,
} from "../../../service/bookingService";
import { fetchAllUserDetails } from "../../../service/SubscriptionWellDhanService";
import { resolveBookingEventTypeId } from "./bookingEventType";
import { confirmedCallsFor, dayRange, slotDayWindow } from "./slotGrid";

/** The Users list's own event type — the same one its screen sends. */
const USER_LIST_EVENT_TYPE_ID = "4850387a-8838-45d5-8c65-dce9d1f86de6";
const CLIENT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const normaliseName = (value) =>
  String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

/** Which subscription coach fields name this department's coach. */
export function coachFieldsFor(departmentTitle) {
  const title = String(departmentTitle || "").toLowerCase();
  if (/nutri|diet/.test(title)) return ["diet_coach_id"];
  if (/fitness|workout/.test(title)) return ["workout_coach_id"];
  return ["diet_coach_id", "workout_coach_id"];
}

/**
 * Whether a user-list row is this coach's client under `field`. By id when
 * the row carries one, else by name: the optimized rows name their coaches
 * and may carry no ids.
 */
function isCoachedBy(row, field, coachId, coachName) {
  const isDiet = field === "diet_coach_id";
  const id = isDiet ? row?.diet_coach_id : row?.workout_coach_id ?? row?.fitness_coach_id;
  if (id) return String(id) === String(coachId);
  const name = isDiet ? row?.diet_coach_name : row?.fitness_coach_name ?? row?.workout_coach_name;
  return Boolean(coachName) && normaliseName(name) === normaliseName(coachName);
}

/**
 * A user-list row → what the picker shows. `fetch-all-users-optimized` names
 * its fields `user_first_name` / `user_phone_number` / `user_email`; the bare
 * names are what other user endpoints send, so both are read.
 */
function toClient(row) {
  const first = row?.user_first_name ?? row?.first_name;
  const last = row?.user_last_name ?? row?.last_name;
  const name =
    [first, last].filter(Boolean).join(" ").trim() ||
    row?.user_name ||
    row?.name ||
    "Unknown user";
  return {
    userId: row?.user_id ?? row?.id,
    name,
    phone: row?.user_phone_number ?? row?.phone_number ?? row?.phone,
    email: row?.user_email ?? row?.email,
    joinedAt: row?.user_joining_date ?? null,
  };
}

const usersOf = (res) => (Array.isArray(res?.data?.users) ? res.data.users : []);

/**
 * Data for the off-shift booking modal: the coach's clients, the picked
 * client's next call type, and the ADD.
 *
 * **Clients.** On the caller's own calendar, the Users list's "My users"
 * request. On another coach's, the list filtered by the subscription coach
 * field for that department, every row re-checked here; a refused filter is
 * retried unfiltered and narrowed the same way (first page only).
 *
 * **That day's calls.** The client may not have an off-shift call on a day
 * they already have a confirmed one. Read per (client, day) from
 * `fetch-booking-records` — never with `user_id`, which is a 403 for anyone
 * else's; the rows are narrowed here. An own calendar reads unnarrowed (the
 * scope predicate returns the caller's calendars, and an executive's every
 * booking); a coach's calendar names its `employee_id`. So a confirmed call
 * with a coach outside the caller's reach is not seen — the backend's own
 * member-overlap check (B8) still refuses an overlapping one.
 *
 * **Call type.** Read from `fetch-coach-slot` for the client and day — only
 * the response-level call type; the slots are the grid clients book, which is
 * exactly what an off-shift call avoids. A 409 naming `department_id` means
 * the coach takes this client's calls for two departments: `needsDepartment`
 * goes up and `setDepartmentId` re-reads with the pick.
 *
 * @param {Object} params
 * @param {string} params.coachId - The calendar's `employee_id`.
 * @param {string} [params.coachName]
 * @param {boolean} params.ownCalendar
 * @param {string} [params.departmentTitle] - The calendar's department, when known.
 * @param {string|null} [params.departmentId]
 * @param {string} [params.coachShiftTypeId] - The "Booking" shift type.
 * @param {string|null} params.userId - The picked client.
 * @param {Date|null} params.date - The picked day.
 */
export function useOffShiftBooking({
  coachId,
  coachName,
  ownCalendar,
  departmentTitle,
  departmentId,
  coachShiftTypeId,
  userId,
  date,
}) {
  const { get, post } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const eventTypeId = useMemo(() => resolveBookingEventTypeId(sessionUtils), [sessionUtils]);

  /* ---------------------------- clients ---------------------------- */

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [clientsAttempt, setClientsAttempt] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    const request = (filters, myUsersOnly) =>
      fetchAllUserDetails(
        USER_LIST_EVENT_TYPE_ID,
        CLIENT_PAGE_SIZE,
        0,
        debouncedSearch || null,
        "user_joining_date",
        "desc",
        filters,
        myUsersOnly,
        { signal: controller.signal }
      );

    const load = async () => {
      if (ownCalendar) {
        const res = await request(null, true);
        if (res.aborted) return null;
        if (!res.success) throw new Error(res.message || "Couldn't load your clients.");
        return usersOf(res);
      }
      const fields = coachFieldsFor(departmentTitle);
      const pages = await Promise.all(
        fields.map(async (field) => {
          let res = await request({ [field]: { equals: coachId } }, false);
          if (res.aborted) return null;
          if (!res.success) res = await request(null, false);
          if (res.aborted) return null;
          if (!res.success) throw new Error(res.message || "Couldn't load this coach's clients.");
          return usersOf(res).filter((row) => isCoachedBy(row, field, coachId, coachName));
        })
      );
      return pages.some((page) => page === null) ? null : pages.flat();
    };

    setClientsLoading(true);
    setClientsError(null);
    load()
      .then((rows) => {
        if (rows === null || controller.signal.aborted) return;
        const byId = new Map();
        rows.map(toClient).forEach((client) => {
          if (client.userId && !byId.has(String(client.userId))) byId.set(String(client.userId), client);
        });
        setClients([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
        setClientsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setClientsError(err);
        setClientsLoading(false);
      });
    return () => controller.abort();
  }, [debouncedSearch, ownCalendar, departmentTitle, coachId, coachName, clientsAttempt]);

  /* --------------------------- call type --------------------------- */

  const [pickedDepartmentId, setDepartmentId] = useState(null);
  const [callType, setCallType] = useState(null);
  const [callTypeLoading, setCallTypeLoading] = useState(false);
  const [callTypeError, setCallTypeError] = useState(null);
  const [needsDepartment, setNeedsDepartment] = useState(false);
  const [callTypeAttempt, setCallTypeAttempt] = useState(0);
  const requestSeq = useRef(0);

  // The department is per client: a new client asks again.
  useEffect(() => {
    setDepartmentId(null);
    setNeedsDepartment(false);
  }, [userId]);

  const dayKey = date instanceof Date ? date.toDateString() : null;
  useEffect(() => {
    setCallType(null);
    setCallTypeError(null);
    if (!userId || !coachId || !eventTypeId || !coachShiftTypeId || !date) {
      setCallTypeLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setCallTypeLoading(true);
    fetchCoachSlots(get, {
      eventTypeId,
      employeeId: coachId,
      coachShiftTypeId,
      userId,
      departmentId: pickedDepartmentId || departmentId || undefined,
      ...slotDayWindow(date),
    })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        if (!data?.shift_call_type_id) {
          setCallTypeError(new Error("No call type came back for this client."));
        } else {
          setCallType({
            shiftCallTypeId: data.shift_call_type_id,
            callNo: data.call_no,
            durationMinutes: Number(data.duration_minutes) || null,
            bufferMinutes: Number(data.buffer_minutes) || 0,
          });
          setNeedsDepartment(false);
        }
        setCallTypeLoading(false);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        if (err?.status === 409 && /department_id/i.test(err?.message || "")) {
          setNeedsDepartment(true);
        } else {
          setCallTypeError(err);
        }
        setCallTypeLoading(false);
      });
  }, [userId, coachId, eventTypeId, coachShiftTypeId, dayKey, pickedDepartmentId, departmentId, callTypeAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------- that day's calls ------------------------- */

  const [dayCalls, setDayCalls] = useState(null);
  const [dayCallsError, setDayCallsError] = useState(null);
  const [dayCallsAttempt, setDayCallsAttempt] = useState(0);
  const daySeq = useRef(0);

  useEffect(() => {
    setDayCalls(null);
    setDayCallsError(null);
    if (!userId || !eventTypeId || !date) return;
    const seq = ++daySeq.current;
    fetchBookingRecords(get, {
      eventTypeId,
      employeeId: ownCalendar ? undefined : coachId,
      ...dayRange(date),
    })
      .then((payload) => {
        if (seq !== daySeq.current) return;
        setDayCalls(confirmedCallsFor(payload?.booking_records, userId));
      })
      .catch((err) => {
        if (seq !== daySeq.current) return;
        setDayCallsError(err);
      });
  }, [userId, eventTypeId, dayKey, ownCalendar, coachId, dayCallsAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------ write ----------------------------- */

  const [saving, setSaving] = useState(false);

  /**
   * Book the call: one ADD with no `coach_shift_id`, which is what marks it
   * off the grid. Rejects with the API's own message.
   */
  const book = useCallback(
    async ({ startsAt, endsAt }) => {
      if (!callType?.shiftCallTypeId) throw new Error("This client's call type hasn't loaded yet.");
      setSaving(true);
      try {
        return await modifyBookings(post, {
          eventTypeId,
          bookings: [
            {
              operation: "ADD",
              coach_id: coachId,
              shift_call_type_id: callType.shiftCallTypeId,
              slot_start: startsAt.toISOString(),
              slot_end: endsAt.toISOString(),
              user_id: userId,
            },
          ],
        });
      } finally {
        setSaving(false);
      }
    },
    [post, eventTypeId, coachId, userId, callType]
  );

  return {
    hasEventType: Boolean(eventTypeId),
    search,
    setSearch,
    clients,
    clientsLoading,
    clientsError,
    retryClients: () => setClientsAttempt((n) => n + 1),
    callType,
    callTypeLoading,
    callTypeError,
    retryCallType: () => setCallTypeAttempt((n) => n + 1),
    /** The client's confirmed calls on the picked day; null until read. */
    dayCalls,
    dayCallsError,
    retryDayCalls: () => setDayCallsAttempt((n) => n + 1),
    needsDepartment,
    pickedDepartmentId,
    setDepartmentId,
    saving,
    book,
  };
}
